/**
 * DetailPageLayout Component
 *
 * Unified layout for all detail pages
 * Provides consistent structure for:
 * - Back navigation
 * - Page title with icon
 * - Metadata/badges
 * - Action buttons or toolbar
 * - Loading/error/empty states
 */

import React from "react";
import PropTypes from "prop-types";
import { useNavigate, Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { DashboardLayout } from "../../../layouts";
import { Badge, Button, RawDataButton, LoadingState } from "../index";
import "./DetailPageLayout.scss";

export const DetailPageLayout = ({
  // Navigation
  backButton,
  backRowRight,

  // Header
  icon: Icon,
  iconColor,
  title,
  subtitle,
  badges = [],

  // Metadata
  metadata = [],

  // Alert banner
  alert,

  // Actions
  toolbar,
  actions = [],

  // States
  loading = false,
  error = null,
  empty = false,
  emptyMessage = "No data found",
  emptyIcon: EmptyIcon,

  // Raw data
  data,

  // Content
  children,
  className = "",
}) => {
  const navigate = useNavigate();

  // Handle back navigation
  const handleBack = () => {
    if (backButton.onClick) {
      backButton.onClick();
    } else if (backButton.to) {
      navigate(backButton.to);
    } else {
      navigate(-1);
    }
  };

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading…" minHeight={400} />
      </DashboardLayout>
    );
  }

  // Empty state
  if (empty) {
    return (
      <DashboardLayout>
        <div className="detail-page-empty">
          <Button
            variant="ghost"
            icon={<FaArrowLeft />}
            onClick={handleBack}
            size="small"
          >
            {backButton.label || "Back"}
          </Button>
          <div className="empty-content">
            {EmptyIcon && <EmptyIcon className="empty-icon" />}
            <p>{emptyMessage}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={`detail-page-layout ${className}`}>
        {/* Header */}
        <div className="detail-page-header">
          <div className="header-main">
            {Icon && (
              <div
                className="header-icon"
                style={{ backgroundColor: iconColor || "#8b5cf6" }}
              >
                <Icon />
              </div>
            )}
            <div className="header-content">
              <div className="title-row">
                <h1>{title}</h1>
                {badges.map((badge, i) => (
                  <Badge
                    key={i}
                    variant={badge.variant}
                    icon={badge.icon}
                    size={badge.size || "medium"}
                  >
                    {badge.label}
                  </Badge>
                ))}
              </div>
              {subtitle && <p className="subtitle">{subtitle}</p>}

              {/* Metadata */}
              {metadata.length > 0 && (
                <div className="metadata">
                  {metadata.map((item, i) => (
                    <div key={i} className="metadata-item">
                      <span className="metadata-label">{item.label}:</span>
                      {item.link ? (
                        <Link
                          to={item.link}
                          className="metadata-value metadata-value--link"
                        >
                          {item.value}
                        </Link>
                      ) : (
                        <span className="metadata-value">{item.value}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Back Button (+ optional right-aligned content on the same line) */}
        <div
          className="detail-page-back"
          style={backRowRight ? { display: "flex", alignItems: "center" } : undefined}
        >
          <Button
            variant="ghost"
            icon={<FaArrowLeft />}
            onClick={handleBack}
            size="small"
          >
            {backButton.label || "Back"}
          </Button>
          {backRowRight && <div style={{ marginLeft: "auto" }}>{backRowRight}</div>}
        </div>

        {/* Toolbar or Actions */}
        {toolbar ? (
          toolbar
        ) : (actions &&
            (Array.isArray(actions) ? actions.length > 0 : actions)) ||
          data ? (
          <div className="detail-page-actions">
            <div style={{ flex: 1 }} />
            {data && <RawDataButton data={data} buttonVariant="secondary" />}
            {actions &&
              (Array.isArray(actions)
                ? actions.map((action, i) => (
                    <Button
                      key={i}
                      variant={action.variant || "secondary"}
                      icon={action.icon ? <action.icon /> : undefined}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      loading={action.loading}
                      size={action.size || "small"}
                    >
                      {action.label}
                    </Button>
                  ))
                : // If actions is a JSX element, render it directly
                  actions)}
          </div>
        ) : null}

        {/* Alert Banner */}
        {alert && (
          <div className="detail-page-alert">
            {alert}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="detail-page-error">
            <p>{error}</p>
          </div>
        )}

        {/* Page Content */}
        <div className="detail-page-body">{children}</div>
      </div>
    </DashboardLayout>
  );
};

DetailPageLayout.propTypes = {
  // Navigation
  backButton: PropTypes.shape({
    label: PropTypes.string,
    to: PropTypes.string,
    onClick: PropTypes.func,
  }).isRequired,
  backRowRight: PropTypes.node,

  // Header
  icon: PropTypes.elementType,
  iconColor: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.string,
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      variant: PropTypes.string,
      icon: PropTypes.elementType,
      size: PropTypes.string,
    })
  ),

  // Metadata
  metadata: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node.isRequired,
      link: PropTypes.string,
    })
  ),

  // Alert banner
  alert: PropTypes.node,

  // Actions
  toolbar: PropTypes.node,
  actions: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.elementType,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        loading: PropTypes.bool,
        variant: PropTypes.string,
        size: PropTypes.string,
      })
    ),
    PropTypes.node, // Also accept JSX elements directly
  ]),

  // States
  loading: PropTypes.bool,
  error: PropTypes.string,
  empty: PropTypes.bool,
  emptyMessage: PropTypes.string,
  emptyIcon: PropTypes.elementType,

  // Raw data
  data: PropTypes.object,

  // Content
  children: PropTypes.node,
  className: PropTypes.string,
};
